import { useEffect, useState } from 'react';
import { Button, Collapse, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type { AddressBreakdown } from '@expeditor/shared';
import { deliveryLegsApi } from '../api/delivery-legs';

/** Есть ли в разбивке хоть одно заполненное поле. */
function hasAnyPart(v: AddressBreakdown): boolean {
  return Object.values(v).some((x) => x != null && String(x).trim() !== '');
}

/**
 * Разбивка адреса на структурированные поля (АдрРФ ЭТрН) — сворачиваемый блок.
 *
 * Разбивка хранится один раз на текст адреса, поэтому её можно вести и в форме
 * ЭТрН, и в карточках сторон (клиент, привлекаемый исполнитель, реквизиты
 * экспедитора). Если поля пусты — в XML идёт адрес текстом.
 */
export function AddressBreakdownFields({
  value,
  onChange,
  withDeliveryExtras = false,
  hint = 'Заполняется по желанию; сохраняется и подставится в следующий раз для того же адреса.',
}: {
  value: AddressBreakdown;
  onChange: (v: AddressBreakdown) => void;
  /** Для адреса доставки — дополнительно комментарий и координаты. */
  withDeliveryExtras?: boolean;
  /** Пояснение под заголовком блока. */
  hint?: string;
}) {
  const filled = hasAnyPart(value);
  const [open, setOpen] = useState(filled);
  // Разбивка может подгрузиться уже после монтирования (карточки сторон грузят
  // её по адресу) — тогда блок разворачиваем, чтобы данные были видны.
  useEffect(() => {
    if (filled) setOpen(true);
  }, [filled]);
  const set = (key: keyof AddressBreakdown) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [key]: e.currentTarget.value || undefined });

  return (
    <Stack gap={4}>
      <Button
        variant="subtle"
        size="compact-xs"
        justify="flex-start"
        leftSection={open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        onClick={() => setOpen((o) => !o)}
        style={{ alignSelf: 'flex-start' }}
      >
        Разбивка адреса (для ЭТрН)
      </Button>
      <Collapse in={open}>
        <Stack gap="xs">
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
          <Group grow>
            <TextInput label="Индекс" value={value.index ?? ''} onChange={set('index')} />
            <TextInput
              label="Код региона"
              placeholder="напр. 50"
              value={value.regionCode ?? ''}
              onChange={set('regionCode')}
            />
            <TextInput label="Район" value={value.district ?? ''} onChange={set('district')} />
          </Group>
          <Group grow>
            <TextInput label="Город" value={value.city ?? ''} onChange={set('city')} />
            <TextInput
              label="Населённый пункт"
              value={value.settlement ?? ''}
              onChange={set('settlement')}
            />
          </Group>
          <Group grow>
            <TextInput label="Улица" value={value.street ?? ''} onChange={set('street')} />
            <TextInput label="Дом" value={value.house ?? ''} onChange={set('house')} />
            <TextInput label="Кв./офис" value={value.flat ?? ''} onChange={set('flat')} />
          </Group>
          <TextInput
            label="Контактный телефон (для ЭПД)"
            placeholder="+7 ..."
            description="Если заполнен — попадёт в ЭТрН и перевозочные документы для этого адреса."
            value={value.phone ?? ''}
            onChange={set('phone')}
          />
          {withDeliveryExtras && (
            <>
              <TextInput
                label="Комментарий к адресу"
                placeholder="напр. на въезде позвонить…"
                value={value.comment ?? ''}
                onChange={set('comment')}
              />
              <Group grow>
                <TextInput label="Широта" value={value.lat ?? ''} onChange={set('lat')} />
                <TextInput label="Долгота" value={value.lon ?? ''} onChange={set('lon')} />
              </Group>
            </>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
}

/**
 * Разбивка адреса для карточки стороны: грузится по сохранённому адресу,
 * редактируется в форме и сохраняется вместе с карточкой (`save`).
 *
 * `savedAddress` — адрес, каким он лежит на сервере (не редактируемое
 * состояние поля): по нему разбивка подтягивается ровно один раз, чтобы ввод
 * пользователя не затирался на каждое нажатие клавиши. Сохраняется разбивка
 * уже под новым текстом адреса.
 */
export function useAddressBreakdown(savedAddress: string | null | undefined) {
  const [parts, setParts] = useState<AddressBreakdown>({});
  const raw = (savedAddress ?? '').trim();

  useEffect(() => {
    let alive = true;
    if (!raw) {
      setParts({});
      return;
    }
    void deliveryLegsApi
      .addressBreakdown(raw)
      .then((p) => {
        if (alive) setParts(p ?? {});
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [raw]);

  /** Сохранить разбивку под указанным адресом (может отличаться от исходного). */
  const save = async (address: string): Promise<void> => {
    const a = (address ?? '').trim();
    if (!a) return;
    await deliveryLegsApi.saveAddressBreakdown(a, parts);
  };

  return { parts, setParts, save };
}
